import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import {
  PPE_INCORRECT_REASON_I18N_KEYS,
  PPE_INCORRECT_REASONS,
  PPE_REPORT_JUSTIFICATION_MAX,
  PpeIncorrectReason,
} from 'app/services/PPE/ppe.models';

/** What the dialog closes with when the user submits; undefined on cancel. */
export interface PpeReportDialogResult {
  reasonCode: PpeIncorrectReason;
  /** Trimmed; empty when the user wrote nothing. */
  justification: string;
}

/** Justification is required (non-blank) only when the reason is Other. */
const justificationRequiredForOther: ValidatorFn = (
  group: AbstractControl,
): ValidationErrors | null => {
  const reason = group.get('reason')?.value;
  const text = String(group.get('justification')?.value ?? '').trim();
  return reason === PpeIncorrectReason.Other && !text ? { justificationRequired: true } : null;
};

/**
 * "Report incorrect detection" dialog for AI PPE observations (V2CORE-3078).
 * Collects a reason (HOME `IncorrectAIDetectionTypes`, 0..3) and a justification that is
 * required for Other (max 500). It does not call the API: the host sends the report.
 *
 * Standalone so any host can open it with `MatDialog.open` without a module declaration.
 */
@Component({
  selector: 'app-ppe-report-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatRadioModule,
    TranslocoModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ppe-report-dialog.component.html',
})
export class PpeReportDialogComponent implements OnDestroy {
  readonly reasons = PPE_INCORRECT_REASONS;
  readonly reasonLabels = PPE_INCORRECT_REASON_I18N_KEYS;
  readonly maxLength = PPE_REPORT_JUSTIFICATION_MAX;
  readonly Other = PpeIncorrectReason.Other;

  readonly form = new FormGroup(
    {
      reason: new FormControl<PpeIncorrectReason | null>(null, Validators.required),
      justification: new FormControl<string>(
        '',
        Validators.maxLength(PPE_REPORT_JUSTIFICATION_MAX),
      ),
    },
    { validators: justificationRequiredForOther },
  );

  private readonly sub: Subscription;

  constructor(
    private dialogRef: MatDialogRef<PpeReportDialogComponent, PpeReportDialogResult>,
    private transloco: TranslocoService,
  ) {
    // Re-check the group rule when the reason changes so the error shows at once.
    this.sub = this.form.controls.reason.valueChanges.subscribe(() =>
      this.form.controls.justification.updateValueAndValidity({ emitEvent: false }),
    );
  }

  get isRtl(): boolean {
    return this.transloco.getActiveLang() === 'ar';
  }

  get isOther(): boolean {
    return this.form.controls.reason.value === PpeIncorrectReason.Other;
  }

  get justificationLength(): number {
    return (this.form.controls.justification.value || '').length;
  }

  get showJustificationRequired(): boolean {
    return (
      this.form.hasError('justificationRequired') &&
      (this.form.controls.justification.touched || this.form.controls.justification.dirty)
    );
  }

  get canSubmit(): boolean {
    return this.form.valid;
  }

  submit(): void {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.dialogRef.close({
      reasonCode: this.form.controls.reason.value as PpeIncorrectReason,
      justification: (this.form.controls.justification.value || '').trim(),
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
